import _ from 'lodash';

import {InboxModel} from '../../../core/src/models/api/inboxModel';
import {PaletteColorsEnum} from '../../../core/src/models/api/paletteModel';
import {collectEnumValues} from '../../../core/src/models/api/types/enumTypes';
import {compareColors, tryParseColor} from '../../../core/src/models/helpers/colorHelpers';
import {inboxHasColor} from '../../../core/src/models/helpers/inboxHelpers';
import {UserTheme} from './themeTypes';

export type PaletteColorShadeFinder = (theme: UserTheme, color: PaletteColorsEnum) => string;

// Convenience methods for looking up a shade by PaletteColorEnum. Useful with
// semantic styles if you need to support multiple palette colors (see tagSemanticStyles.)
export const findColorShade10: PaletteColorShadeFinder = (theme, color) => theme.palette[color].shade10;
export const findColorShade20: PaletteColorShadeFinder = (theme, color) => theme.palette[color].shade20;
export const findColorShade30: PaletteColorShadeFinder = (theme, color) => theme.palette[color].shade30;
export const findColorShade40: PaletteColorShadeFinder = (theme, color) => theme.palette[color].shade40;
export const findColorShade50: PaletteColorShadeFinder = (theme, color) => theme.palette[color].shade50;
const legacyToPaletteColorsEnumMap: Record<
  string,
  (theme: UserTheme) => {key: PaletteColorsEnum; ref: string}
> = {
  '#FFFFFF': (theme) => ({key: PaletteColorsEnum.GREY, ref: theme.accent[PaletteColorsEnum.GREY].highlight}),
  '#E15151': (theme) => ({key: PaletteColorsEnum.RED, ref: theme.accent[PaletteColorsEnum.RED].highlight}),
  '#E19951': (theme) => ({
    key: PaletteColorsEnum.ORANGE,
    ref: theme.accent[PaletteColorsEnum.ORANGE].highlight,
  }),
  '#E1E151': (theme) => ({
    key: PaletteColorsEnum.YELLOW,
    ref: theme.accent[PaletteColorsEnum.YELLOW].highlight,
  }),
  '#99E151': (theme) => ({
    key: PaletteColorsEnum.GREEN,
    ref: theme.accent[PaletteColorsEnum.GREEN].highlight,
  }),
  '#51E151': (theme) => ({
    key: PaletteColorsEnum.GREEN,
    ref: theme.accent[PaletteColorsEnum.GREEN].highlight,
  }),
  '#51E199': (theme) => ({
    key: PaletteColorsEnum.GREEN,
    ref: theme.accent[PaletteColorsEnum.GREEN].highlight,
  }),
  '#51E1E1': (theme) => ({key: PaletteColorsEnum.TEAL, ref: theme.accent[PaletteColorsEnum.TEAL].highlight}),
  '#5199E1': (theme) => ({key: PaletteColorsEnum.BLUE, ref: theme.accent[PaletteColorsEnum.BLUE].highlight}),
  '#5151E1': (theme) => ({key: PaletteColorsEnum.BLUE, ref: theme.accent[PaletteColorsEnum.BLUE].highlight}),
  '#9951E1': (theme) => ({
    key: PaletteColorsEnum.PURPLE,
    ref: theme.accent[PaletteColorsEnum.PURPLE].highlight,
  }),
  '#E151E1': (theme) => ({key: PaletteColorsEnum.PINK, ref: theme.accent[PaletteColorsEnum.PINK].highlight}),
  '#E15199': (theme) => ({key: PaletteColorsEnum.PINK, ref: theme.accent[PaletteColorsEnum.PINK].highlight}),
};

export function legacyInboxColorToPaletteColor(inboxColor: string, theme: UserTheme): PaletteColorsEnum {
  const exactMatch = legacyToPaletteColorsEnumMap[inboxColor.toUpperCase()];

  if (exactMatch) {
    return exactMatch(theme).key;
  }

  const parsedColor = tryParseColor(inboxColor);

  if (!parsedColor) {
    return PaletteColorsEnum.GREY;
  }

  const closestColor = _(legacyToPaletteColorsEnumMap)
    .values()
    .sortBy((val) => compareColors(tryParseColor(val(theme).ref)!, parsedColor))
    .first();

  if (!closestColor) {
    return PaletteColorsEnum.GREY;
  }

  return closestColor(theme).key;
}

export function resolveInboxColor(inbox: Pick<InboxModel, 'color'>, theme: UserTheme) {
  return theme.accent[resolveInboxPaletteColor(inbox, theme)].highlight;
}

export function resolveInboxPaletteColor(
  inbox: Pick<InboxModel, 'color'>,
  theme: UserTheme,
): PaletteColorsEnum {
  const shade = inboxHasColor(inbox)
    ? legacyInboxColorToPaletteColor(inbox.color, theme)
    : PaletteColorsEnum.GREY;

  return shade;
}

export function colorStringToPaletteColor(
  colorString: string,
  theme: UserTheme,
  colorValues?: ReadonlyArray<PaletteColorsEnum>,
): PaletteColorsEnum | undefined {
  return (colorValues ?? collectEnumValues(PaletteColorsEnum)).find(
    (value) => colorString.toLowerCase() === theme.palette[value].shade40,
  );
}
